import {
  faArrowsRotate,
  faCamera,
  faCheck,
  faCircleCheck,
  faCircleXmark,
  faEye,
  faFileArrowUp,
  faFileLines,
  faImage,
  faTrashCan,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Alert from 'src/components/ui/Alert';
import AttachmentRow from 'src/components/ui/AttachmentRow';
import Button from 'src/components/ui/Button';
import ButtonRow from 'src/components/ui/ButtonRow';
import Input from 'src/components/ui/Input';
import Select from 'src/components/ui/Select';
import Textarea from 'src/components/ui/Textarea';
import UploadDropzone from 'src/components/ui/UploadDropzone';
import {
  ALLOWED_DOCUMENT_UPLOAD_FORMATS,
  WORK_INSTRUCTION_REPORT_UNITS_VERBOSE,
  WORK_INSTRUCTION_TYPES,
} from 'src/utils/constants';
import {
  isWorkInstructionValidByThreshold,
  WORK_INSTRUCTION_THRESHOLD_ACTIONS,
} from 'src/utils/thresholds';
import truncateMiddle from 'src/utils/truncateMiddle';
import isViewableDocument from 'src/utils/viewableDocument';
import { formatStorageReport, parseStorageReport } from 'src/utils/workInstructionReports';

/**
 * Input widget for one checklist instruction, switching on report_type —
 * mirrors the run work-instruction widget set (types, threshold rules, units,
 * upload handling) rebuilt on the shared ui-kit (Input, Textarea, Select,
 * Button, Alert).
 *
 * `onChange(value)` updates the live value; `onCommit(value)` signals the value
 * is ready to persist (blur for free-text/number/range, change for discrete).
 */

/* Threshold feedback — same rules and copy as the run work-instructions:
 * valid = quiet check; invalid = warning or error per threshold_action.
 * Warnings are feedback only; Errors also block completion (RunEvent). */
const ThresholdHint = ({ instruction, value }) => {
  const hasValue = Array.isArray(value)
    ? value.some(item => item !== '' && item != null)
    : value !== '' && value != null;
  if (!hasValue || !instruction.threshold) return null;
  const isValid = isWorkInstructionValidByThreshold(
    value,
    instruction.threshold,
    instruction.thresholdType
  );
  if (isValid) {
    return (
      <div className='mnt-threshold-ok'>
        <FontAwesomeIcon icon={faCircleCheck} /> Within the defined thresholds
      </div>
    );
  }
  if (instruction.thresholdAction === WORK_INSTRUCTION_THRESHOLD_ACTIONS.ERROR) {
    return (
      <Alert variant='danger' className='mt-2 mb-0' icon={faCircleXmark} title='Error'>
        The entered value falls outside of the defined thresholds.
      </Alert>
    );
  }
  let warning = 'The entered value falls outside of the defined thresholds. Proceed with caution.';
  if (instruction.thresholdAction === WORK_INSTRUCTION_THRESHOLD_ACTIONS.NCR_WARNING) {
    warning =
      'The entered value falls outside of the defined thresholds. Review whether NCR is required.';
  } else if (instruction.thresholdAction === WORK_INSTRUCTION_THRESHOLD_ACTIONS.REMFG_WARNING) {
    warning =
      'The entered value falls outside of the defined thresholds. Review whether remanufacture is required.';
  }
  return (
    <Alert variant='warning' className='mt-2 mb-0' icon={faTriangleExclamation} title='Warning'>
      {warning}
    </Alert>
  );
};

ThresholdHint.propTypes = {
  instruction: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
};

ThresholdHint.defaultProps = { value: null };

const NumberField = ({ value, unit, onChange, onBlur, placeholder }) => (
  <div className={classNames('mnt-unit-field', { 'mnt-unit-field--suffixed': Boolean(unit) })}>
    <Input
      type='number'
      step='any'
      inputMode='decimal'
      value={value ?? ''}
      placeholder={placeholder || '0.0'}
      onChange={event => onChange(event.target.value)}
      onBlur={event => onBlur(event.target.value)}
    />
    {unit && (
      <span className='mnt-unit-field__unit'>
        {WORK_INSTRUCTION_REPORT_UNITS_VERBOSE[unit] || unit}
      </span>
    )}
  </div>
);

NumberField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
};

NumberField.defaultProps = { value: '', unit: null, onBlur: () => {}, placeholder: null };

const StoredAtField = ({ value, onChange, onCommit }) => {
  const [data, setData] = useState(() => parseStorageReport(value));

  const update = (field, next) => {
    const updated = { ...data, [field]: next };
    setData(updated);
    const stringified = formatStorageReport(updated);
    if (stringified) onChange(stringified);
  };

  const commit = () => {
    const stringified = formatStorageReport(data);
    if (stringified) onCommit(stringified);
  };

  return (
    <>
      <ButtonRow>
        <Input
          placeholder='Amount'
          value={data.amount}
          onChange={event => update('amount', event.target.value)}
          onBlur={commit}
        />
        <Input
          placeholder='Location'
          value={data.location}
          onChange={event => update('location', event.target.value)}
          onBlur={commit}
        />
      </ButtonRow>
      <div className='mnt-field-help'>Stored [amount] at [location]</div>
    </>
  );
};

StoredAtField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
};

StoredAtField.defaultProps = { value: '' };

const isImageFilename = name => /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)(\?|$)/i.test(name || '');

/* Picture/document field: drop zone → upload progress → saved attachment row
 * (tap to view, Replace re-opens the picker). Pictures keep a local preview;
 * the row icon reflects the actual file kind (image vs document). */
const FileField = ({
  instruction,
  isPicture,
  reportFile,
  uploading,
  onPick,
  onViewFile,
  onClear,
}) => {
  const [preview, setPreview] = useState(null);
  const inputId = `mnt-file-${instruction.uuid}`;
  const accept = isPicture
    ? 'image/*'
    : ALLOWED_DOCUMENT_UPLOAD_FORMATS.map(format => `.${format}`).join(',');

  const handleChange = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (isPicture && file.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.addEventListener('load', ({ target }) => setPreview(target.result));
      reader.readAsDataURL(file);
    }
    onPick(file);
  };

  const fileInput = (
    <input
      id={inputId}
      type='file'
      accept={accept}
      className='d-none'
      onClick={event => {
        event.target.value = null;
      }}
      onChange={handleChange}
    />
  );

  if (uploading != null) {
    return <UploadDropzone progress={uploading} />;
  }

  if (reportFile) {
    const viewable =
      Boolean(reportFile.uri) && (!reportFile.filename || isViewableDocument(reportFile.filename));
    return (
      <AttachmentRow
        thumbnail={isPicture && preview ? preview : null}
        icon={isPicture || isImageFilename(reportFile.filename) ? faImage : faFileLines}
        name={truncateMiddle(reportFile.filename || 'Attachment', 30)}
        viewable={viewable}
        sub={
          <>
            Saved
            {viewable && (
              <>
                {' · '}
                <FontAwesomeIcon icon={faEye} className='attachment__sub-eye' /> tap to view
              </>
            )}
          </>
        }
        actions={
          <>
            {!instruction.required && (
              <button
                type='button'
                className='attachment__delete'
                onClick={() => {
                  setPreview(null);
                  onClear();
                }}
              >
                <FontAwesomeIcon icon={faTrashCan} /> Delete
              </button>
            )}
            <label htmlFor={inputId} className='attachment__replace'>
              <FontAwesomeIcon icon={faArrowsRotate} /> Replace
              {fileInput}
            </label>
          </>
        }
        onView={() => onViewFile(reportFile)}
      />
    );
  }

  return (
    <UploadDropzone
      htmlFor={inputId}
      icon={isPicture ? faCamera : faFileArrowUp}
      label={isPicture ? 'Tap to take a picture' : 'Tap to upload a document'}
      hint={isPicture ? null : 'PDF, DOC, XLS, images and more'}
    >
      {fileInput}
    </UploadDropzone>
  );
};

FileField.propTypes = {
  instruction: PropTypes.object.isRequired,
  isPicture: PropTypes.bool.isRequired,
  reportFile: PropTypes.object,
  uploading: PropTypes.number,
  onPick: PropTypes.func.isRequired,
  onViewFile: PropTypes.func.isRequired,
  onClear: PropTypes.func,
};

FileField.defaultProps = { reportFile: null, uploading: null, onClear: () => {} };

const InstructionInput = ({
  instruction,
  value,
  onChange,
  onCommit,
  reportFile,
  onViewFile,
  onClear,
  uploading,
}) => {
  const { reportType, reportUnits, choices } = instruction;

  switch (reportType) {
    case WORK_INSTRUCTION_TYPES.NO_ENTRY:
      return value === true ? (
        <div className='text-success font-weight-bold'>
          <FontAwesomeIcon icon={faCheck} /> Acknowledged
        </div>
      ) : (
        <Button
          variant='primary'
          size='small'
          icon={<FontAwesomeIcon icon={faCheck} />}
          onClick={() => {
            onChange(true);
            onCommit(true);
          }}
        >
          Acknowledge
        </Button>
      );

    case WORK_INSTRUCTION_TYPES.CHECKBOX:
      return (
        <ButtonRow>
          <Button
            block
            fill={value === true}
            variant={value === true ? 'success' : 'default'}
            icon={<FontAwesomeIcon icon={faCheck} />}
            onClick={() => {
              onChange(true);
              onCommit(true);
            }}
          >
            Yes
          </Button>
          <Button
            block
            fill={value === false}
            variant={value === false ? 'danger' : 'default'}
            icon={<FontAwesomeIcon icon={faXmark} />}
            onClick={() => {
              onChange(false);
              onCommit(false);
            }}
          >
            No
          </Button>
        </ButtonRow>
      );

    case WORK_INSTRUCTION_TYPES.NUMBER:
      return (
        <>
          <NumberField
            value={value}
            unit={reportUnits}
            onChange={onChange}
            onBlur={next => onCommit(next)}
          />
          <ThresholdHint instruction={instruction} value={value} />
        </>
      );

    case WORK_INSTRUCTION_TYPES.RANGE: {
      const range = Array.isArray(value) ? value : ['', ''];
      const isBoundFilled = bound => bound !== '' && bound != null;
      const setRange = (index, next) => {
        const updated = [...range];
        updated[index] = next;
        onChange(updated);
      };
      // A range answer is atomic: persist only a complete (or fully cleared)
      // pair. A half-entered range would be stored but read back as "None"
      // everywhere the answer is displayed.
      const commitRange = (index, next) => {
        const updated = [...range];
        updated[index] = next;
        if (updated.filter(isBoundFilled).length === 1) return;
        onCommit(updated);
      };
      const halfFilled = range.filter(isBoundFilled).length === 1;
      return (
        <>
          <ButtonRow>
            <NumberField
              value={range[0]}
              unit={reportUnits}
              placeholder='min'
              onChange={next => setRange(0, next)}
              onBlur={next => commitRange(0, next)}
            />
            <NumberField
              value={range[1]}
              unit={reportUnits}
              placeholder='max'
              onChange={next => setRange(1, next)}
              onBlur={next => commitRange(1, next)}
            />
          </ButtonRow>
          {halfFilled ? (
            <div className='mnt-range-incomplete'>Enter both min and max to record this range.</div>
          ) : (
            <ThresholdHint instruction={instruction} value={value} />
          )}
        </>
      );
    }

    case WORK_INSTRUCTION_TYPES.TEXT:
      return (
        <Textarea
          rows={2}
          value={value ?? ''}
          placeholder='Add a note…'
          onChange={event => onChange(event.target.value)}
          onBlur={event => onCommit(event.target.value)}
        />
      );

    case WORK_INSTRUCTION_TYPES.SINGLE_SELECT_DROPDOWN:
      return (
        <Select
          variant='field'
          name={`mnt-instr-${instruction.uuid}`}
          value={value ?? ''}
          onChange={event => {
            onChange(event.target.value);
            onCommit(event.target.value);
          }}
        >
          {instruction.required ? (
            <option disabled value=''>
              Choose…
            </option>
          ) : (
            <option value=''>None</option>
          )}
          {(choices || []).map(choice => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </Select>
      );

    case WORK_INSTRUCTION_TYPES.OUTPUT_AT_LOCATION:
      return <StoredAtField value={value} onChange={onChange} onCommit={onCommit} />;

    case WORK_INSTRUCTION_TYPES.PICTURE:
    case WORK_INSTRUCTION_TYPES.DOCUMENT:
      return (
        <FileField
          instruction={instruction}
          isPicture={reportType === WORK_INSTRUCTION_TYPES.PICTURE}
          reportFile={reportFile}
          uploading={uploading}
          onViewFile={onViewFile}
          onClear={onClear}
          onPick={file => {
            onChange(file);
            onCommit(file);
          }}
        />
      );

    default:
      return (
        <Input
          value={value ?? ''}
          onChange={event => onChange(event.target.value)}
          onBlur={event => onCommit(event.target.value)}
        />
      );
  }
};

InstructionInput.propTypes = {
  instruction: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func,
  reportFile: PropTypes.object,
  onViewFile: PropTypes.func,
  onClear: PropTypes.func,
  uploading: PropTypes.number,
};

InstructionInput.defaultProps = {
  value: null,
  onCommit: () => {},
  reportFile: null,
  onViewFile: () => {},
  onClear: () => {},
  uploading: null,
};

export default InstructionInput;
